import Configs from '@/Configs';
import { buildUnauthenticatedPulseSurveyQuery } from '@/views/survey/buildUnauthenticatedPulseSurveyQuery';

// --------------------------------------------
// Global 401 (unauthorized) handler – e.g. clear auth state and redirect to login.
// Set from App.jsx so it can call authLogout and sessionStorage.clear().
// --------------------------------------------
let globalUnauthHandler = null;

export function setGlobalUnauthHandler(handler) {
  globalUnauthHandler = typeof handler === 'function' ? handler : null;
}

function runGlobalUnauthHandler() {
  try {
    if (globalUnauthHandler) globalUnauthHandler();
  } catch (e) {
    if (Configs.devEnvironment) console.warn('[apiCalls] globalUnauthHandler error:', e);
  }
  sessionStorage.clear();
}

// --------------------------------------------
// Handle API calls
// --------------------------------------------
export const handleResponse = async (response, responseType = 'json') => {
  if (Configs.devEnvironment) console.log('[apiCalls.js][handleResponse] response: ', response);

  if (response.status === 200 || response.status === 201 || response.status === 204) {
    try {
      if (responseType === 'blob') {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.style.display = 'none';
        link.href = url;

        // Extract filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'download.csv'; // fallback filename

        if (Configs.devEnvironment) {
          console.log(
            '[apiCalls.js][handleResponse] Content-Disposition header:',
            contentDisposition
          );
          console.log('[apiCalls.js][handleResponse] All response headers:');
          for (let [key, value] of response.headers.entries()) {
            console.log(`  ${key}: ${value}`);
          }
        }

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+?)"/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1];
            if (Configs.devEnvironment)
              console.log('[apiCalls.js][handleResponse] Extracted filename (quoted):', filename);
          } else {
            // Try without quotes
            const filenameMatchNoQuotes = contentDisposition.match(/filename=([^;]+)/);
            if (filenameMatchNoQuotes && filenameMatchNoQuotes[1]) {
              filename = filenameMatchNoQuotes[1].trim();
              if (Configs.devEnvironment)
                console.log(
                  '[apiCalls.js][handleResponse] Extracted filename (unquoted):',
                  filename
                );
            }
          }
        } else {
          if (Configs.devEnvironment)
            console.log('[apiCalls.js][handleResponse] No Content-Disposition header found');
        }

        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        URL.revokeObjectURL(url);

        return { status: response.status, message: 'File downloaded successfully' };
      } else {
        const responseBody = await response?.json();
        return responseBody;
      }
    } catch (error) {
      if (response.status === 204) {
        return 204;
      }

      const auxResponse = {
        status: response.status,
        error: error,
      };
      throw auxResponse;
    }
  } else {
    const status = response?.status;
    if (status === 401) {
      // Do not run global logout/redirect for /authenticate – caller handles expired cookie (e.g. show login).
      const isAuthenticateCall = response?.url?.includes('/authenticate');
      if (!isAuthenticateCall) {
        runGlobalUnauthHandler();
      }
      return status;
    }
    if (status === 429) {
      try {
        const body = await response.json();
        return { status: 429, message: body?.message ?? null };
      } catch (_) {
        return { status: 429, message: null };
      }
    }
    return status;
  }
};

const getCsrfToken = () => {
  return document?.cookie
    ?.split('; ')
    ?.find((row) => row?.startsWith('csrfToken'))
    ?.split('=')[1];
};

export const fetchOptions = (
  method,
  data = null,
  token = null,
  responseType = 'json',
  correlationId = null
) => {
  const csrfToken = getCsrfToken();

  const headers = new Headers();

  // Only set JSON headers if data is not FormData
  const isFormData = data instanceof FormData;

  if (!isFormData) {
    headers.append('Content-Type', 'application/json');
    if (method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE') {
      headers.append('Accept', 'application/json');
      if (csrfToken) headers.append('CSRF-TOKEN', csrfToken);
    }
  }

  // Ensure token is a non-empty string and trim (JWT must be sent as-is, no extra whitespace)
  const bearerToken =
    typeof token === 'string'
      ? token.trim()
      : token && typeof token === 'object' && typeof token.value === 'string'
        ? token.value.trim()
        : null;
  if (bearerToken) {
    if (bearerToken.split('.').length !== 3 && Configs.devEnvironment) {
      console.warn('[apiCalls.js] Authorization token does not look like a JWT (expected 3 parts). Check login response shape.');
    }
    headers.append('Authorization', `Bearer ${bearerToken}`);
  }

  if (correlationId) {
    headers.append('X-Correlation-ID', correlationId);
  }

  const options = {
    method,
    headers,
    credentials: 'include', // if needed for cookies/cross-origin requests
  };

  options.responseType = responseType; // 'json', 'text', 'blob', etc.

  if (data) {
    options.body = isFormData ? data : JSON.stringify(data);
  }

  return options;
};

// --------------------------------------------
// API calls - export
// --------------------------------------------
// AUTHENTICATION
// AUTHENTICATE TOKEN
export const authenticateToken = async (companyID, employeeID, token, correlationId = null) => {
  try {
    const response = await fetch(
      Configs.authenticate,
      fetchOptions('POST', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][authenticateToken()] We could not reach the server:', error);
    return 500;
  }
};

// LOGIN - BEARER
export const logIn = async (companyID, employeeID, token, loginBody, correlationId = null) => {
  try {
    const response = await fetch(
      Configs.loginAPI,
      fetchOptions('POST', loginBody, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][logIn()] We could not reach the server:', error);
    return 500;
  }
};

// RESET PASSWORD
export const resetPassword = async (
  companyID,
  employeeID,
  token,
  resetPasswordBody,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.resetPasswordAPI,
      fetchOptions('POST', resetPasswordBody, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][resetPassword()] We could not reach the server:', error);
    return 500;
  }
};

// CHANGE PASSWORD
export const updatePassword = async (
  companyID,
  employeeID,
  token,
  updatePasswordBody,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.updatePasswordAPI,
      fetchOptions('POST', updatePasswordBody, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][updatePassword()] We could not reach the server:', error);
    return 500;
  }
};

// LOGOUT
export const logout = async (companyID, employeeID, token, correlationId = null) => {
  try {
    const response = await fetch(
      Configs.logoutAPI + '?employeeID=' + employeeID + '&companyID=' + companyID,
      fetchOptions('POST', {}, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][logout()] We could not reach the server:', error);
    return 500;
  }
};

// --------------------------------------------
// ORGS and TEAMS
// --------------------------------------------
// Directs
export const getEmployeesOfMgr = async (companyID, employeeID, token, correlationId = null) => {
  try {
    const response = await fetch(
      Configs.platformGetEmployeesOfManagerAPI +
      '?employeeID=' +
      employeeID +
      '&companyID=' +
      companyID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getEmployeesOfMgr()] We could not reach the server:', error);
    return 500;
  }
};
// Org of Manager
export const getOrgOfMgr = async (companyID, employeeID, token, correlationId = null) => {
  try {
    const response = await fetch(
      Configs.platformGetOrgOfManagerAPI + '?employeeID=' + employeeID + '&companyID=' + companyID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getOrgOfMgr()] We could not reach the server:', error);
    return 500;
  }
};

// --------------------------------------------
// HOME
// --------------------------------------------
// GET PENDING TO DOs
export const getPendingToDos = async (companyID, employeeID, token, correlationId = null) => {
  try {
    const response = await fetch(
      Configs.platformGetPendingToDosAPI + '?companyID=' + companyID + '&employeeID=' + employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getPendingToDos()] We could not reach the server:', error);
    return 500;
  }
};

// UPDATE TO DO STATUS
export const putUpdateToDoStatus = async (
  companyID,
  employeeID,
  token,
  statusBody,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPutUpdateNotificationStatus +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('PUT', statusBody, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][putUpdateToDoStatus()] We could not reach the server:', error);
    return 500;
  }
};

// GET KEY KPIs
export const getKeyKPIs = async (companyID, employeeID, token, correlationId = null) => {
  try {
    const response = await fetch(
      Configs.platformGetKeyKPIsForEmployeeAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getPendingToDos()] We could not reach the server:', error);
    return 500;
  }
};

// --------------------------------------------
// ORGANIGRAM
// --------------------------------------------
// CREATE EMPLOYEE
export const postNewEmployee = async (
  companyID,
  employeeID,
  token,
  employeeObject,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPostNewEmployeeAPI + '?companyID=' + companyID + '&employeeID=' + employeeID,
      fetchOptions('POST', employeeObject, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][postNewEmployee()] We could not reach the server:', error);
    return 500;
  }
};

// EDIT EMPLOYEE
export const putEditEmployee = async (
  companyID,
  employeeID,
  token,
  employeeObject,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPutEmployeeAPI + '?companyID=' + companyID + '&employeeID=' + employeeID,
      fetchOptions('PUT', employeeObject, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][putEditEmployee()] We could not reach the server:', error);
    return 500;
  }
};

// GET ORGANIGRAM CSV
export const getOrganigramCSV = async (companyID, employeeID, token, correlationId = null) => {
  try {
    const response = await fetch(
      Configs.platformGetOrganigramCSVAPI + '?employeeID=' + employeeID + '&companyID=' + companyID,
      fetchOptions('GET', null, token, 'blob', correlationId)
    );
    return await handleResponse(response, 'blob');
  } catch (error) {
    console.error('[apiCalls.js][getOrganigramCSV()] We could not reach the server:', error);
    return 500;
  }
};

// SUBMIT REORGANIZATION
export const postOrganigramReorganization = async (
  companyID,
  employeeID,
  token,
  reorganizationFileID,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPostOrganigramReorganizationAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&reorganizationFileID=' +
      reorganizationFileID,
      fetchOptions(
        'POST',
        {
          companyID: companyID,
          employeeID: employeeID,
          reorganizationFileID: reorganizationFileID,
        },
        token,
        'json',
        correlationId
      )
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][postOrganigramReorganization()] We could not reach the server:',
      error
    );
    return 500;
  }
};

// --------------------------------------------
// DAYS OFF / TIME OFF
// --------------------------------------------
// GET TEAM PENDING REQUESTS
export const getHolidaysTeamPendingRequests = async (
  companyID,
  employeeID,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetTimeOffPendingRequestsForManagerAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getHolidaysTeamPendingRequests()] We could not reach the server:',
      error
    );
    return 500;
  }
};

// POST NEW HOLIDAY REQUEST
export const postNewHolidayRequest = async (
  companyID,
  employeeID,
  token,
  holidayRequest,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPostTimeOffAPI + '?companyID=' + companyID + '&employeeID=' + employeeID,
      fetchOptions('POST', holidayRequest, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][postNewHolidayRequest()] We could not reach the server:', error);
    return 500;
  }
};

// GET HOLIDAYS FOR EMPLOYEE
export const getHolidaysForEmployee = async (
  companyID,
  employeeID,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetTimeOffAPI + '?companyID=' + companyID + '&employeeID=' + employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getHolidaysForEmployee()] We could not reach the server:', error);
    return 500;
  }
};

// AWARD DAYS TO EMPLOYEE
export const postTimeOffAwardDaysToEmployee = async (
  companyID,
  employeeID,
  token,
  data,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPutTimeOffAwardTimeOffToEmployeeAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('POST', data, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][postTimeOffAwardDaysToEmployee()] We could not reach the server:',
      error
    );
    return 500;
  }
};

// --------------------------------------------
// HR INBOX
// --------------------------------------------
// GET HR INBOX MESSAGES FROM EMPLOYEE
export const getHRInboxMessagesForEmployee = async (
  companyID,
  employeeID,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetInboxMessagesForEmployeeAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getHRInboxMessages()] We could not reach the server:', error);
    return 500;
  }
};

// DELETE MESSAGE FROM EMPLOYEE
export const deleteHRInboxMessageForEmployee = async (
  companyID,
  employeeID,
  inboxID,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformDeleteInboxMessageAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&inboxID=' +
      inboxID,
      fetchOptions('DELETE', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][deleteHRInboxMessageForEmployee()] We could not reach the server:',
      error
    );
    return 500;
  }
};

// GET HR INBOX MESSAGES FOR HR
export const getHRInboxMessagesForHR = async (
  companyID,
  employeeID,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetInboxMessagesForCompanyAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getHRInboxMessagesForHR()] We could not reach the server:', error);
    return 500;
  }
};

// POST NEW HR INBOX MESSAGE
export const postNewHRInboxMessage = async (
  companyID,
  employeeID,
  token,
  messageBody,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPostInboxRequestAPI + '?companyID=' + companyID + '&employeeID=' + employeeID,
      fetchOptions('POST', messageBody, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][postNewHRInboxMessage()] We could not reach the server:', error);
    return 500;
  }
};

// GET HR INBOX REQUEST PROFILE
export const getHRInboxRequest = async (
  companyID,
  employeeID,
  requestID,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetInboxRequestAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&requestID=' +
      requestID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getHRInboxRequest()] We could not reach the server:', error);
    return 500;
  }
};

// PUT / UPDATE HR INBOX REQUEST
export const putHRInboxRequest = async (
  companyID,
  employeeID,
  requestID,
  token,
  requestBody,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPutInboxRequestAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&requestID=' +
      requestID,
      fetchOptions('PUT', requestBody, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][putHRInboxRequest()] We could not reach the server:', error);
    return 500;
  }
};

// --------------------------------------------
// PERFORMANCE
// --------------------------------------------
// GET PERSONAL PERFORMANCE DATA
export const getPersonalPerformanceData = async (
  companyID,
  employeeID,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetPerformanceCampaignsHistoryAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getPersonalPerformanceData()] We could not reach the server:',
      error
    );
    return 500;
  }
};

// GET LIVE PERFORMANCE CAMPAIGNS
export const getLivePerformanceCampaigns = async (
  companyID,
  employeeID,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetLivePerformanceCampaignsAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getLivePerformanceCampaigns()] We could not reach the server:',
      error
    );
    return 500;
  }
};

// SUBMIT FEEDBACK PROVIDERS
export const submitFeedbackProviders = async (
  companyID,
  employeeID,
  token,
  feedbackProviders,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPostFeedbackProvidersAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('POST', feedbackProviders, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][submitFeedbackProviders()] We could not reach the server:', error);
    return 500;
  }
};

// DELETE SINGLE FEEDBACK PROVIDER
export const deleteSingleFeedbackProvider = async (
  companyID,
  employeeID,
  token,
  campaignID,
  feedbackProviderEmail,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformDeleteFeedbackProviderAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&campaignID=' +
      campaignID +
      '&feedbackProviderEmail=' +
      feedbackProviderEmail,
      fetchOptions('DELETE', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][deleteSingleFeedbackProvider()] We could not reach the server:',
      error
    );
    return 500;
  }
};

// EMPLOYEE DRAFT
export const getPerformanceReviewDraft = async (
  companyID,
  employeeID,
  token,
  campaignID,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetPerformanceDraftsAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&campaignID=' +
      campaignID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getPerformanceReviewDraft()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const submitPerformanceReviewSelfFeedback = async (
  companyID,
  employeeID,
  token,
  reviewData,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformSubmitSelfFeedbackAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('POST', reviewData, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][submitPerformanceReviewSelfFeedback()] We could not reach the server:',
      error
    );
    return 500;
  }
};

// MANAGER DRAFT
export const getPerformanceReviewManagerDraft = async (
  companyID,
  employeeID,
  token,
  campaignID,
  managerID,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetManagerPerformanceDraftsAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&campaignID=' +
      campaignID +
      '&managerID=' +
      managerID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getPerformanceReviewManagerDraft()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const submitPerformanceReviewManagerFeedback = async (
  companyID,
  employeeID,
  token,
  reviewData,
  managerID,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformSubmitManagerFeedbackAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&managerID=' +
      managerID,
      fetchOptions('POST', reviewData, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][submitPerformanceReviewManagerFeedback()] We could not reach the server:',
      error
    );
    return 500;
  }
};

// FEEDBACKS DRAFT
export const getPerformanceReviewFeedbacksDraft = async (
  companyID,
  employeeID,
  token,
  campaignID,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetPerformanceFeedbacksDraftsAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&campaignID=' +
      campaignID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getPerformanceReviewFeedbacksDraft()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const submitFeedbackForEmployee = async (
  companyID,
  employeeID,
  token,
  feedbackData,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPostPeerFeedbackAPI + '?companyID=' + companyID + '&employeeID=' + employeeID,
      fetchOptions('POST', feedbackData, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][submitPerformanceReviewManagerFeedback()] We could not reach the server:',
      error
    );
    return 500;
  }
};

// PERFORMANCE HISTORY
export const getPerformanceReviewHistory = async (
  companyID,
  employeeID,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetPerformanceCampaignsHistoryAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getPerformanceReviewHistory()] We could not reach the server:',
      error
    );
    return 500;
  }
};

// GET FEEDBACK PROVIDERS OF CAMPAIGN
export const getPerformanceFeedbackProviders = async (
  companyID,
  employee,
  token,
  campaignID,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetPerformanceFeedbackProvidersAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employee +
      '&campaignID=' +
      campaignID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getFeedbackProviders()] We could not reach the server:', error);
    return 500;
  }
};

export const getPerformanceFeedbacks = async (
  companyID,
  employeeID,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetPerformanceFeedbacksAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getPerformanceFeedbacks()] We could not reach the server:', error);
    return 500;
  }
};

export const getPerformancePeerFeedback = async (
  companyID,
  employeeID,
  peerCampaignID,
  selfEmail,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetPerformancePeerFeedbackAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&campaignID=' +
      peerCampaignID +
      '&feedbackProviderEmail=' +
      selfEmail,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getPerformanceFeedbacks()] We could not reach the server:', error);
    return 500;
  }
};

// GET HR LIVE CAMPAIGN REPORT
export const getPerformanceReviewStatusByCompany = async (
  companyID,
  employeeID,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetPerformanceReviewStatusByCompanyAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getPerformanceReviewStatusByCompany()] We could not reach the server:',
      error
    );
    return 500;
  }
};

// DOWNLOAD HR PERFORMANCE REVIEW AS CSV
export const downloadPerformanceReviewAsCSV = async (
  companyID,
  employeeID,
  campaignID,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetCSVDownloadPerformanceCampaignAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&campaignID=' +
      campaignID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response, 'blob');
  } catch (error) {
    console.error(
      '[apiCalls.js][downloadPerformanceReviewAsCSV()] We could not reach the server:',
      error
    );
    return 500;
  }
};

// GET SPECIFIC CAMPAIGN REVIEW
export const getSpecificPerformanceReview = async (
  companyID,
  employeeID,
  token,
  campaignID,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetSpecificPerformanceReviewAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&campaignID=' +
      campaignID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getSpecificPerformanceReview()] We could not reach the server:',
      error
    );
    return 500;
  }
};

// GET SPECIFIC PERFORMANCE REVIEW BY STATUS AND EMPLOYEE
export const getPerformanceReviewsByStatusAndEmployee = async (
  companyID,
  employeeID,
  token,
  status,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetPerformanceReviewByStatusAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&status=' +
      status,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getSpecificPerformanceReviewByStatusAndEmployee()] We could not reach the server:',
      error
    );
    return 500;
  }
};

// SUBMIT PERFORMANCE REVIEW WITH EMPLOYEE
export const sharePerformanceReview = async (
  companyID,
  employeeID,
  token,
  reviewData,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformSubmitManagerFeedbackAPI,
      fetchOptions('POST', reviewData, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][sharePerformanceReview()] We could not reach the server:', error);
    return 500;
  }
};

// --------------------------------------------
// CALIBRATION
// --------------------------------------------
export const getCalibrationData = async (companyID, employeeID, token, correlationId = null) => {
  try {
    const response = await fetch(
      Configs.platformGetCalibrationCampaignsAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getCalibrationData()] We could not reach the server:', error);
    return 500;
  }
};

export const getExistingCalibrationRatingsForEmployee = async (
  companyID,
  employeeID,
  token,
  campaignID,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetCalibrationExistingRatingsForEmployeeAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&campaignID=' +
      campaignID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getExistingCalibrationRatings()] We could not reach the server:',
      error
    );
    return 500;
  }
};

/**
 * GET the latest submitted/completed calibration for `targetEmployeeID`.
 *
 * Two URL shapes are produced depending on `excludeCampaignID`:
 *
 *   - When `excludeCampaignID` is a non-empty string, the request is
 *     `...?...&excludeCampaignID=<encoded>` and the backend filters that
 *     campaign out of the "last finalized" result (live mode: exclude the
 *     active cycle so we don't return the in-progress row as "previous").
 *   - When `excludeCampaignID` is `null` or `""`, the segment is **omitted
 *     entirely** and the backend returns the most recent
 *     `submitted`/`completed` row regardless of campaign (past-view:
 *     between-campaigns read-only state on calibration home).
 *
 * @param {string|number} companyID
 * @param {string|number} actingEmployeeID
 * @param {string|number} targetEmployeeID
 * @param {string|null} excludeCampaignID Mongo ObjectId hex, `null`, or `""`.
 * @param {string} token
 * @param {string|null} [correlationId]
 */
export const getCalibrationLastFinalizedForEmployee = async (
  companyID,
  actingEmployeeID,
  targetEmployeeID,
  excludeCampaignID,
  token,
  correlationId = null
) => {
  try {
    const hasExclude =
      typeof excludeCampaignID === 'string' && excludeCampaignID.length > 0;
    const baseUrl =
      Configs.platformGetCalibrationLastFinalizedForEmployeeAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      actingEmployeeID +
      '&targetEmployeeID=' +
      targetEmployeeID;
    const url = hasExclude
      ? baseUrl + '&excludeCampaignID=' + encodeURIComponent(excludeCampaignID)
      : baseUrl;

    const response = await fetch(
      url,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getCalibrationLastFinalizedForEmployee()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const submitEmployeeCalibrationRatings = async (
  companyID,
  employeeID,
  token,
  ratingsBody,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPostCalibrationRatingForEmployeeAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('POST', ratingsBody, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][submitEmployeeCalibrationRatings()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const postConfirmCalibrationAsManager = async (
  companyID,
  employeeID,
  token,
  campaignID,
  body,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPostCalibrationCampaignAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&campaignID=' +
      campaignID,
      fetchOptions('POST', body, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][postConfirmCalibrationAsManager()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const getCalibrationPublicResultForPerformanceCampaign = async (
  companyID,
  employeeID,
  performanceCampaignID,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetCalibrationPublicResultForPerformanceCampaignAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&performanceCampaignID=' +
      performanceCampaignID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getCalibrationPublicResultForPerformanceCampaign()] We could not reach the server:',
      error
    );
    return 500;
  }
};

// --------------------------------------------
// DEVELOPMENT
// --------------------------------------------
export const getDevelopmentPlan = async (
  companyID,
  employeeID,
  campaignID,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetDevelopmentPlanAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&campaignID=' +
      campaignID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    return 500;
  }
};

export const postDevelopmentPlan = async (
  companyID,
  employeeID,
  token,
  developmentPlanBody,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPostDevelopmentPlanAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('POST', developmentPlanBody, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][postDevelopmentPlan()] We could not reach the server:', error);
    return 500;
  }
};

export const generateDevelopmentPlan = async (
  companyID,
  employeeID,
  managerID,
  token,
  campaignID = null,
  correlationId = null
) => {
  try {
    const body = {
      companyID: companyID,
      employeeID: employeeID,
      managerID: managerID,
      campaignID: campaignID,
    };
    const response = await fetch(
      Configs.platformPostGenerateDevelopmentPlanAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('POST', body, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][generateDevelopmentPlan()] We could not reach the server:', error);
    return 500;
  }
};

// --------------------------------------------
// RECOGNITION
// --------------------------------------------
export const getPublicSingleRecognition = async (recognitionID, correlationId = null) => {
  try {
    const response = await fetch(
      Configs.platformGetPublicSingleRecognitionAPI + '?recognitionID=' + recognitionID,
      fetchOptions(
        'GET',
        null,
        import.meta.env.VITE_UNAUTHENTICATED_REQUESTS_API_TOKEN,
        'json',
        correlationId
      )
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getPublicSingleRecognition()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const getPublicTrustCenterReport = async (publicId, correlationId = null) => {
  try {
    const response = await fetch(
      Configs.platformGetPublicTrustCenterAPI + '?publicId=' + encodeURIComponent(publicId),
      fetchOptions(
        'GET',
        null,
        import.meta.env.VITE_UNAUTHENTICATED_REQUESTS_API_TOKEN,
        'json',
        correlationId
      )
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getPublicTrustCenterReport()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const getPublicTrustCenterStatement = async (publicId, correlationId = null) => {
  try {
    const response = await fetch(
      Configs.platformGetPublicTrustCenterStatementAPI +
        '?publicId=' +
        encodeURIComponent(publicId),
      fetchOptions(
        'GET',
        null,
        import.meta.env.VITE_UNAUTHENTICATED_REQUESTS_API_TOKEN,
        'json',
        correlationId
      )
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getPublicTrustCenterStatement()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const postPlus1Recognition = async (
  companyID,
  employeeID,
  token,
  recognitionID,
  body,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPostPlus1RecognitionAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&recognitionID=' +
      recognitionID,
      fetchOptions('POST', body, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][postPlus1Recognition()] We could not reach the server:', error);
    return 500;
  }
};

// --------------------------------------------
// REWARDS
// --------------------------------------------
export const getRewardsDataForCompany = async (
  companyID,
  employeeID,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetRewardsDataForCompanyAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getRewardsDataForCompany()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const postCashOutRewardForEmployee = async (
  companyID,
  employeeID,
  token,
  rewardData,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPostCashRewardOutForEmployeeAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('POST', rewardData, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][postCashOutRewardForEmployee()] We could not reach the server:',
      error
    );
    return 500;
  }
};

// --------------------------------------------
// PULSE
// --------------------------------------------
// POST PULSE CAMPAIGN PROFILE REPORTING DATA (filters + visibility, same pattern as question/driver profile)
export const getPulseCampaignProfileReportingData = async (
  companyID,
  employeeID,
  campaignID,
  token,
  correlationId = null,
  filtersBody = {},
  visibility = undefined
) => {
  try {
    const visibilityQuery =
      visibility != null && String(visibility).trim() !== ''
        ? `&visibility=${encodeURIComponent(String(visibility))}`
        : '';
    const response = await fetch(
      Configs.platformGetPulseCampaignIDAnswersDirectsAPI +
        '?companyID=' +
        companyID +
        '&employeeID=' +
        employeeID +
        '&campaignID=' +
        campaignID +
        visibilityQuery,
      fetchOptions('POST', { filters: filtersBody ?? {} }, token, 'json', correlationId)
    );
    if (Configs.devEnvironment)
      console.log('[apiCalls.js][getPulseCampaignProfileReportingData()] response: ', response);
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getPulseCampaignProfileReportingData()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const getPulseCampaignAISummary = async (
  companyID,
  employeeID,
  campaignID,
  token,
  correlationId = null,
  filtersBody = {}
) => {
  try {
    const response = await fetch(
      Configs.platformGetPulseSurveyAISummaryAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&campaignID=' +
      campaignID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    if (Configs.devEnvironment)
      console.log('[apiCalls.js][getPulseCampaignAISummary()] response: ', response);
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getPulseCampaignAISummary()] We could not reach the server:',
      error
    );
    return 500;
  }
};

// GET PULSE REPORTING DATA (i.e.: aggregated by manager data) - so we can filter by team - and also by category, subcategory of questions
export const postQuestionsReportingData = async (
  companyID,
  employeeID,
  token,
  correlationId = null,
  filtersBody = {}
) => {
  try {
    const response = await fetch(
      Configs.pulseQuestionsReportingDataAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('POST', filtersBody, token, 'json', correlationId)
    );
    if (Configs.devEnvironment)
      console.log('[apiCalls.js][getQuestionsReportingData()] response: ', response);
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getQuestionsReportingData()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const getPulseHomeGraphFrontendData = async (
  companyID,
  employeeID,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetPulseHomeGraphFrontendDataAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getPulseHomeGraphFrontendData()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const getPulseKeyIndicators = async (
  companyID,
  employeeID,
  token,
  correlationId = null,
  body
) => {
  try {
    const response = await fetch(
      Configs.platformGetPulseKeyIndicatorsAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('POST', body, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getPulseKeyIndicators()] We could not reach the server:', error);
    return 500;
  }
};

// GET DAILY QUESTION
export const getPulseDailyQuestion = async (
  companyID,
  employeeID,
  token,
  questionDate,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPulseGetDailyQuestionAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&questionDate=' +
      questionDate,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getDailyQuestion()] We could not reach the server:', error);
    return 500;
  }
};

// GET ALL PULSE QUESTIONS
export const getAllPulseQuestions = async (companyID, employeeID, token, correlationId = null) => {
  try {
    const response = await fetch(
      Configs.pulseGetAllQuestionsAPI + '?companyID=' + companyID + '&employeeID=' + employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getAllPulseQuestions()] We could not reach the server:', error);
    return 500;
  }
};

// GET PULSE SURVEY
export const unauthenticatedGetPulseSurvey = async (
  companyID,
  employeeID,
  token,
  campaignID,
  questionDate = null,
  correlationId = null,
  signedSearch = null
) => {
  try {
    const query = buildUnauthenticatedPulseSurveyQuery(signedSearch, {
      companyID,
      employeeID,
      campaignID,
      questionDate,
    });
    const url = Configs.platformUnauthenticatedPulseGetSurveyAPI + query;

    const response = await fetch(
      url,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][unauthenticatedGetPulseSurvey()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const unauthenticatedPostPulseSurveyAnswer = async (
  companyID,
  employeeID,
  token,
  campaignID,
  questionDate,
  answerData,
  correlationId = null,
  signedSearch = null
) => {
  try {
    const query = buildUnauthenticatedPulseSurveyQuery(signedSearch, {
      companyID,
      employeeID,
      campaignID,
      questionDate,
    });
    const response = await fetch(
      Configs.pulsePostSurveyAnswerAPI + query,
      fetchOptions('POST', answerData, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][unauthenticatedPostPulseSurveyAnswer()] We could not reach the server:',
      error
    );
    return 500;
  }
};

// GET QUESTION PROFILE
export const getQuestionProfile = async (
  companyID,
  employeeID,
  token,
  questionID,
  correlationId = null,
  body
) => {
  try {
    const response = await fetch(
      Configs.platformGetPulseQuestionProfileAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&questionID=' +
      questionID,
      fetchOptions('POST', body, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getQuestionProfile()] We could not reach the server:', error);
    return 500;
  }
};

// GET DRIVER PROFILE
export const getDriverProfile = async (
  companyID,
  employeeID,
  token,
  driver,
  correlationId = null,
  body
) => {
  try {
    const response = await fetch(
      Configs.platformGetPulseDriverProfileAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&driver=' +
      encodeURIComponent(driver),
      fetchOptions('POST', body, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getDriverProfile()] We could not reach the server:', error);
    return 500;
  }
};

// DRIVERS REPORTING AND HEATMAP
export const getDriversReporting = async (
  companyID,
  employeeID,
  token,
  correlationId = null,
  data
) => {
  try {
    const response = await fetch(
      Configs.platformGetPulseDriversReportingAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('POST', data, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getDriverReporting()] We could not reach the server:', error);
    return 500;
  }
};

// RESPONSE RATE REPORTING
export const getResponseRateReporting = async (
  companyID,
  employeeID,
  token,
  correlationId = null,
  body
) => {
  try {
    const response = await fetch(
      Configs.platformGetPulseResponseRateReportingAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('POST', body, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getPulseResponseRateReporting()] We could not reach the server:',
      error
    );
    return 500;
  }
};

// EMPLOYEE ENGAGEMENT REPORTING
export const getEmployeeEngagementReporting = async (
  companyID,
  employeeID,
  token,
  correlationId = null,
  body
) => {
  try {
    const response = await fetch(
      Configs.platformGetPulseIndividualEmployeesEngagementAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('POST', body, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getEmployeeEngagementReporting()] We could not reach the server:',
      error
    );
    return 500;
  }
};

// INITIATE ANONYMOUS CONVERSATION
export const postPulseInitiateAnonymousConversation = async (
  companyID,
  employeeID,
  token,
  conversationData,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPostPulseInitiateAnonymousConversationAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('POST', conversationData, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][postPulseInitiateAnonymousConversation()] We could not reach the server:',
      error
    );
    return 500;
  }
};

// GET PULSE FILTERS FOR DASHBOARD OR QUESTIONS
export const getPulseFilters = async (companyID, employeeID, token, correlationId = null) => {
  try {
    const response = await fetch(
      Configs.platformGetPulseAvailableFiltersAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getPulseFilters()] We could not reach the server:', error);
    return 500;
  }
};

// --------------------------------------------
// PROFILE
// --------------------------------------------
export const getEmployeeProfile = async (companyID, employeeID, token, correlationId = null) => {
  try {
    const response = await fetch(
      Configs.platformGetProfileAPI + '?companyID=' + companyID + '&employeeID=' + employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getEmployeeProfile()] We could not reach the server:', error);
    return 500;
  }
};

export const uploadProfilePicture = async (
  companyID,
  employeeID,
  token,
  formData,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPostProfilePictureAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('POST', formData, token, 'json', correlationId, true)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][uploadProfilePicture()] We could not reach the server:', error);
    return 500;
  }
};

// --------------------------------------------
// 1:1s - MEETINGS
// --------------------------------------------

/**
 * Append query param only when value is a finite integer (backend: optional().isInt()).
 * Omits null, undefined, empty string, and literal "null" / "undefined" strings.
 * @param {URLSearchParams} searchParams
 * @param {string} key
 * @param {unknown} value
 */
function appendFiniteIntQueryParam(searchParams, key, value) {
  if (value === null || value === undefined) return;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '' || trimmed === 'null' || trimmed === 'undefined') return;
  }
  const n = typeof value === 'number' ? value : parseInt(String(value).trim(), 10);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return;
  searchParams.set(key, String(n));
}

export const getSpace = async (
  companyID,
  callerEmployeeID,
  token,
  { targetEmployeeID, managerID, limit } = {},
  correlationId = null
) => {
  try {
    const searchParams = new URLSearchParams();
    searchParams.set('companyID', String(companyID));
    searchParams.set('employeeID', String(callerEmployeeID));
    appendFiniteIntQueryParam(searchParams, 'targetEmployeeID', targetEmployeeID);
    appendFiniteIntQueryParam(searchParams, 'managerID', managerID);
    const pageLimit =
      limit != null && Number.isFinite(Number(limit)) ? Math.min(100, Math.max(1, Number(limit))) : 100;
    searchParams.set('limit', String(pageLimit));
    const response = await fetch(
      Configs.platformGetMeetingsByEmployeeAPI + '?' + searchParams.toString(),
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getSpace()] We could not reach the server:', error);
    return 500;
  }
};

/**
 * Delete a 1:1 meeting for an employee.
 *
 * Uses the shared fetchOptions/handleResponse helpers so callers can rely on
 * consistent API error handling and status codes.
 *
 * @param {number} companyID - Company identifier.
 * @param {number} employeeID - Authenticated employee performing the delete.
 * @param {string} token - Bearer token for authenticated requests.
 * @param {string} meetingID - MongoDB ObjectId of the meeting to delete.
 * @param {string|null} [correlationId=null] - Optional correlation identifier for tracing.
 * @returns {Promise<*>} - Parsed response body or numeric error code from handleResponse.
 */
export const deleteMeeting = async (
  companyID,
  employeeID,
  token,
  meetingID,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformDeleteMeetingAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&meetingID=' +
      meetingID,
      fetchOptions('DELETE', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][deleteMeeting()] We could not reach the server:', error);
    return 500;
  }
};

export const postNewMeeting = async (
  companyID,
  employeeID,
  token,
  meetingData,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPostMeetingAPI + '?companyID=' + companyID + '&employeeID=' + employeeID,
      fetchOptions('POST', meetingData, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][postNewMeeting()] We could not reach the server:', error);
    return 500;
  }
};

// Alias for clarity in views using the single-meeting editor
export const postMeeting = postNewMeeting;

export const getMeeting = async (
  companyID,
  callerEmployeeID,
  targetEmployeeID,
  managerID,
  meetingID,
  token,
  correlationId = null
) => {
  try {
    const searchParams = new URLSearchParams();
    searchParams.set('companyID', String(companyID));
    searchParams.set('employeeID', String(callerEmployeeID));
    appendFiniteIntQueryParam(searchParams, 'targetEmployeeID', targetEmployeeID);
    appendFiniteIntQueryParam(searchParams, 'managerID', managerID);
    searchParams.set('meetingID', String(meetingID));
    const response = await fetch(
      Configs.platformGetMeetingAPI + '?' + searchParams.toString(),
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getMeeting()] We could not reach the server:', error);
    return 500;
  }
};

// --------------------------------------------
// ONBOARDING
// --------------------------------------------
export const getOnboardingReportingData = async (
  companyID,
  employeeID,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetOnboardingReportingDataAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getPulseReportingData()] We could not reach the server:', error);
    return 500;
  }
};

export const getOnboardingEligibleEmployees = async (
  companyID,
  employeeID,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetOnboardingEligibleCandidatesAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getOnboardingEligibleEmployees()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const getOnboardingTemplates = async (
  companyID,
  employeeID,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetOnboardingPlanTemplatesAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getOnboardingTemplates()] We could not reach the server:', error);
    return 500;
  }
};

export const postOnboardingTemplate = async (
  companyID,
  employeeID,
  token,
  data,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPostOnboardingTemplateAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('POST', data, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][postOnboardingTemplate()] We could not reach the server:', error);
    return 500;
  }
};

export const deleteOnboardingTemplate = async (
  companyID,
  employeeID,
  token,
  data,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformDeleteOnboardingPlanTemplateAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('DELETE', data, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][deleteOnboardingTemplate()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const getOnboardingPlanForEmployee = async (
  companyID,
  employeeID,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetOnboardingPlanForEmployeeAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getOnboardingPlanForEmployee()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const postOnboardingPlanSubsectionComplete = async (
  companyID,
  employeeID,
  token,
  data,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPostOnboardingPlanSubsectionCompleteAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('POST', data, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][postOnboardingPlanSubsectionComplete()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const deleteOnboardingPlan = async (
  companyID,
  employeeID,
  token,
  data,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformDeleteOnboardingPlanAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('DELETE', data, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][deleteOnboardingPlan()] We could not reach the server:', error);
    return 500;
  }
};

// --------------------------------------------
// CAREER
// --------------------------------------------
export const getJobLevellingGuidelinesData = async (
  companyID,
  employeeID,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetCareerJobGuidelinesAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getJobLevellingGuidelinesData()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const postJobLevellingGuideline = async (
  companyID,
  employeeID,
  token,
  jobGuidelineData,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPostCareerJobGuidelineAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('POST', jobGuidelineData, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][postJobLevelleingGuideline()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const putJobLevellingGuideline = async (
  companyID,
  employeeID,
  token,
  jobGuidelineData,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPutCareerJobGuidelineAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('PUT', jobGuidelineData, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][putJobLevellingGuideline()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const postNewCareerPathForEmployee = async (
  companyID,
  employeeID,
  token,
  careerPathData,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPostCareerPathForEmployeeAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('POST', careerPathData, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][postNewCareerPathForEmployee()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const postNewCareerConversation = async (
  companyID,
  employeeID,
  token,
  careerConversationData,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPostCareerConversationAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('POST', careerConversationData, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][postNewCareerConversation()] We could not reach the server:',
      error
    );
    return 500;
  }
};

// --------------------------------------------
// FILES MANAGEMENT
// --------------------------------------------
export const getPublicURLForFile = async (
  companyID,
  employeeID,
  token,
  fileKey,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetPublicURLForFileAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&fileKey=' +
      fileKey,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getPublicURLForFile()] We could not reach the server:', error);
    return 500;
  }
};

// --------------------------------------------
// SETTINGS
// --------------------------------------------
export const getCompanyKAISettings = async (companyID, employeeID, token, correlationId = null) => {
  try {
    const response = await fetch(
      Configs.platformGetKAICompanyPreferencesAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getCompanySettings()] We could not reach the server:', error);
    return 500;
  }
};

export const uploadKAIFile = async (
  companyID,
  employeeID,
  fileType,
  token,
  formData,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPostUploadKAIFileAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&type=' +
      fileType,
      fetchOptions('POST', formData, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][indexKAICompanyDocuments()] Error indexing KAI company documents:',
      error
    );
    return 500;
  }
};

export const indexKAICompanyDocuments = async (
  companyID,
  employeeID,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetIndexKAIDocumentsAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][indexKAICompanyDocuments()] Error indexing KAI company documents:',
      error
    );
    return 500;
  }
};

// --------------------------------------------
// SUPER ADMIN
// --------------------------------------------
export const getCCOaaSReport = async (companyID, employeeID, token, correlationId = null) => {
  try {
    const response = await fetch(
      Configs.platformGetCCOaaSReportAPI + '?companyID=' + companyID + '&employeeID=' + employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getCCOaaSReport()] We could not reach the server:', error);
    return 500;
  }
};

export const getCCOaaSCompanyDetailsAIAnalysis = async (
  companyID,
  employeeID,
  token,
  selectedCompanyID = 1,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetCCOaaSCompanyDetailsAIAnalysisAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&selectedCompanyID=' +
      selectedCompanyID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getCCOaaSCompanyDetailsAIAnalysis()] We could not reach the server:',
      error
    );
    return 500;
  }
};

// --------------------------------------------
// CULTURE
// --------------------------------------------
export const getCultureDriversMonthlyReporting = async (
  companyID,
  employeeID,
  token,
  correlationId = null,
  body
) => {
  try {
    const response = await fetch(
      Configs.platformGetCultureDriversMonthlyReportingAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&reportMonth=' +
      body.reportMonth +
      '&reportYear=' +
      body.reportYear,
      fetchOptions('POST', body, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getCultureDriversMonthlyReporting()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const getCulturePerformanceKPIs = async (
  companyID,
  employeeID,
  token,
  correlationId = null,
  body
) => {
  try {
    const response = await fetch(
      Configs.platformGetCulturePerformanceKPIsAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('POST', body, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getCultureDriversMonthlyReporting()] We could not reach the server:',
      error
    );
    return 500;
  }
};

// --------------------------------------------
// LEARNING
// --------------------------------------------
export const getLearningCoursesCatalogue = async (
  companyID,
  employeeID,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetLearningCoursesCatalogueAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getLearningCoursesCatalogue()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const getLearningCourseDetails = async (
  companyID,
  employeeID,
  token,
  courseID,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetLearningCourseDetailsAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&courseID=' +
      courseID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getLearningCourseDetails()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const updateLearningCourse = async (
  companyID,
  employeeID,
  token,
  courseID,
  body,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPutLearningCourseAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&courseID=' +
      courseID,
      fetchOptions('PUT', body, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][updateLearningCourse()] We could not reach the server:', error);
    return 500;
  }
};

export const assignLearningCourse = async (
  companyID,
  employeeID,
  token,
  body,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPostLearningCourseAssignmentAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('POST', body, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][assignLearningCourse()] We could not reach the server:', error);
    return 500;
  }
};

export const getLearningEmployeeAssignments = async (
  companyID,
  employeeID,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetLearningEmployeeAssignmentsAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getLearningEmployeeAssignments()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const getLearningCourseBlocks = async (
  companyID,
  employeeID,
  token,
  courseID,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetLearningCourseBlocksAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&courseID=' +
      courseID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getLearningCourseBlocks()] We could not reach the server:', error);
    return 500;
  }
};

export const createLearningCourseBlock = async (
  companyID,
  employeeID,
  token,
  courseID,
  body,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPostLearningCourseBlockAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&courseID=' +
      courseID,
      fetchOptions('POST', body, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][createLearningCourseBlock()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const updateLearningCourseBlock = async (
  companyID,
  employeeID,
  token,
  courseID,
  blockID,
  body,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPutLearningCourseBlockAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&courseID=' +
      courseID +
      '&blockID=' +
      blockID,
      fetchOptions('PUT', body, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][updateLearningCourseBlock()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const deleteLearningCourseBlock = async (
  companyID,
  employeeID,
  token,
  courseID,
  blockID,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformDeleteLearningCourseBlockAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&courseID=' +
      courseID +
      '&blockID=' +
      blockID,
      fetchOptions('DELETE', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][deleteLearningCourseBlock()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const getLearningCourseProgress = async (
  companyID,
  employeeID,
  token,
  courseID,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetLearningCourseProgressAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&courseID=' +
      courseID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getLearningCourseProgress()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const createLearningCourseProgress = async (
  companyID,
  employeeID,
  token,
  courseID,
  body,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPostLearningCourseProgressAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&courseID=' +
      courseID,
      fetchOptions('POST', body, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][createLearningCourseProgress()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const updateLearningCourseProgress = async (
  companyID,
  employeeID,
  token,
  courseID,
  body,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPutLearningCourseProgressAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&courseID=' +
      courseID,
      fetchOptions('PUT', body, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][updateLearningCourseProgress()] We could not reach the server:',
      error
    );
    return 500;
  }
};

export const updateLearningBlockProgress = async (
  companyID,
  employeeID,
  token,
  courseID,
  blockID,
  body,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPutLearningBlockProgressAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      '&courseID=' +
      courseID +
      '&blockID=' +
      blockID,
      fetchOptions('PUT', body, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][updateLearningBlockProgress()] We could not reach the server:',
      error
    );
    return 500;
  }
};

// --------------------------------------------
// KAI
// --------------------------------------------
export const sendMessageToKAI = async (
  companyID,
  employeeID,
  token,
  messageData,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPostKAIMessageAPI + '?companyID=' + companyID + '&employeeID=' + employeeID,
      fetchOptions('POST', messageData, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][sendMessageToKAI()] We could not reach the server:', error);
    return 500;
  }
};

// --------------------------------------------
// AI
// --------------------------------------------
export const getOnboardingSurveysAISummary = async (
  companyID,
  employeeID,
  token,
  surveyData,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPOSTOnboardingSurveysAISummaryAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID,
      fetchOptions('POST', surveyData, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getOnboardingSurveysAISummary()] We could not reach the server:',
      error
    );
    return 500;
  }
};

// --------------------------------------------
// GOALS
// --------------------------------------------
// Get Goals
export const getGoals = async (
  companyID,
  employeeID,
  token,
  filters = {},
  correlationId = null
) => {
  try {
    const filterParams = new URLSearchParams();
    if (filters.goalType) filterParams.append('goalType', filters.goalType);
    if (filters.goalTypes) filterParams.append('goalTypes', filters.goalTypes);
    if (filters.listScope) filterParams.append('listScope', filters.listScope);
    if (filters.status) filterParams.append('status', filters.status);
    if (filters.area) filterParams.append('area', filters.area);
    if (filters.subarea) filterParams.append('subarea', filters.subarea);
    if (filters.employeeID) filterParams.append('employeeID', filters.employeeID);
    if (filters.parentPicker) filterParams.append('parentPicker', String(filters.parentPicker));
    if (filters.limit != null) filterParams.append('limit', String(filters.limit));
    if (filters.offset != null) filterParams.append('offset', String(filters.offset));

    const queryString = filterParams.toString();
    const url =
      Configs.platformGetGoalsAPI +
      '?companyID=' +
      companyID +
      '&employeeID=' +
      employeeID +
      (queryString ? '&' + queryString : '');

    const response = await fetch(url, fetchOptions('GET', null, token, 'json', correlationId));
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getGoals()] We could not reach the server:', error);
    return 500;
  }
};

// Get Single Goal
export const getGoal = async (companyID, goalID, employeeID, token, correlationId = null) => {
  try {
    const response = await fetch(
      Configs.platformGetGoalAPI +
      '?companyID=' +
      companyID +
      '&goalID=' +
      goalID +
      '&employeeID=' +
      employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getGoal()] We could not reach the server:', error);
    return 500;
  }
};

// Create Goal
export const postGoal = async (companyID, employeeID, token, goalData, correlationId = null) => {
  try {
    const response = await fetch(
      Configs.platformPostGoalAPI + '?companyID=' + companyID + '&employeeID=' + employeeID,
      fetchOptions('POST', { goal: goalData }, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][postGoal()] We could not reach the server:', error);
    return 500;
  }
};

// Update Goal
export const putGoal = async (
  companyID,
  goalID,
  employeeID,
  token,
  goalData,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPutGoalAPI +
      '?companyID=' +
      companyID +
      '&goalID=' +
      goalID +
      '&employeeID=' +
      employeeID,
      fetchOptions('PUT', { goal: goalData }, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][putGoal()] We could not reach the server:', error);
    return 500;
  }
};

// Delete Goal
export const deleteGoal = async (companyID, goalID, employeeID, token, correlationId = null) => {
  try {
    const response = await fetch(
      Configs.platformDeleteGoalAPI +
      '?companyID=' +
      companyID +
      '&goalID=' +
      goalID +
      '&employeeID=' +
      employeeID,
      fetchOptions('DELETE', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][deleteGoal()] We could not reach the server:', error);
    return 500;
  }
};

// Get Goal Categories
export const getGoalCategories = async (companyID, employeeID, token, correlationId = null) => {
  try {
    const params = new URLSearchParams({ companyID, employeeID });
    const response = await fetch(
      Configs.platformGetGoalCategoriesAPI + '?' + params.toString(),
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getGoalCategories()] We could not reach the server:', error);
    return 500;
  }
};

// Get Goal Settings
export const getGoalSettings = async (companyID, employeeID, token, correlationId = null) => {
  try {
    const params = new URLSearchParams({ companyID, employeeID });
    const response = await fetch(
      Configs.platformGetGoalSettingsAPI + '?' + params.toString(),
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getGoalSettings()] We could not reach the server:', error);
    return 500;
  }
};

// Update Goal Settings
export const putGoalSettings = async (companyID, employeeID, token, settingsData, correlationId = null) => {
  try {
    const params = new URLSearchParams({ companyID, employeeID });
    const response = await fetch(
      Configs.platformPutGoalSettingsAPI + '?' + params.toString(),
      fetchOptions('PUT', { settings: settingsData }, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][putGoalSettings()] We could not reach the server:', error);
    return 500;
  }
};

// Get Goal Hierarchy
export const getGoalHierarchy = async (
  companyID,
  goalID,
  employeeID,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformGetGoalHierarchyAPI +
        '?companyID=' +
        companyID +
        '&goalID=' +
        goalID +
        '&employeeID=' +
        employeeID,
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getGoalHierarchy()] We could not reach the server:', error);
    return 500;
  }
};

// Get Goal Updates (paginated — prefer `@/views/goals/core/apiCalls`)
export const getGoalUpdates = async (
  companyID,
  goalID,
  employeeID,
  token,
  correlationId = null,
  pagination = { limit: 10 }
) => {
  try {
    const qs = new URLSearchParams();
    qs.set('companyID', String(companyID));
    qs.set('goalID', String(goalID));
    qs.set('employeeID', String(employeeID));
    qs.set('limit', String(pagination?.limit ?? 10));
    if (pagination?.cursor != null && String(pagination.cursor).trim() !== '') {
      qs.set('cursor', String(pagination.cursor));
    }
    const response = await fetch(
      Configs.platformGetGoalUpdatesAPI + '?' + qs.toString(),
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getGoalUpdates()] We could not reach the server:', error);
    return 500;
  }
};

// Create Goal Update
export const postGoalUpdate = async (
  companyID,
  goalID,
  employeeID,
  token,
  updateData,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPostGoalUpdateAPI +
      '?companyID=' +
      companyID +
      '&goalID=' +
      goalID +
      '&employeeID=' +
      employeeID,
      fetchOptions('POST', { update: updateData }, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][postGoalUpdate()] We could not reach the server:', error);
    return 500;
  }
};

// Update Goal Update
export const putGoalUpdate = async (
  companyID,
  updateID,
  employeeID,
  token,
  updateData,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPutGoalUpdateAPI +
      '?companyID=' +
      companyID +
      '&updateID=' +
      updateID +
      '&employeeID=' +
      employeeID,
      fetchOptions('PUT', { update: updateData }, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][putGoalUpdate()] We could not reach the server:', error);
    return 500;
  }
};

// Delete Goal Update
export const deleteGoalUpdate = async (
  companyID,
  updateID,
  employeeID,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformDeleteGoalUpdateAPI +
      '?companyID=' +
      companyID +
      '&updateID=' +
      updateID +
      '&employeeID=' +
      employeeID,
      fetchOptions('DELETE', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][deleteGoalUpdate()] We could not reach the server:', error);
    return 500;
  }
};

// Get Goal Comments (paginated — prefer `@/views/goals/core/apiCalls`)
export const getGoalComments = async (
  companyID,
  goalID,
  employeeID,
  token,
  correlationId = null,
  pagination = { limit: 10 }
) => {
  try {
    const qs = new URLSearchParams();
    qs.set('companyID', String(companyID));
    qs.set('goalID', String(goalID));
    qs.set('employeeID', String(employeeID));
    qs.set('limit', String(pagination?.limit ?? 10));
    if (pagination?.cursor != null && String(pagination.cursor).trim() !== '') {
      qs.set('cursor', String(pagination.cursor));
    }
    const response = await fetch(
      Configs.platformGetGoalCommentsAPI + '?' + qs.toString(),
      fetchOptions('GET', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][getGoalComments()] We could not reach the server:', error);
    return 500;
  }
};

// Create Goal Comment
export const postGoalComment = async (
  companyID,
  goalID,
  employeeID,
  token,
  commentData,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPostGoalCommentAPI +
      '?companyID=' +
      companyID +
      '&goalID=' +
      goalID +
      '&employeeID=' +
      employeeID,
      fetchOptions('POST', { comment: commentData }, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][postGoalComment()] We could not reach the server:', error);
    return 500;
  }
};

// Update Goal Comment
export const putGoalComment = async (
  companyID,
  commentID,
  employeeID,
  token,
  commentData,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformPutGoalCommentAPI +
      '?companyID=' +
      companyID +
      '&commentID=' +
      commentID +
      '&employeeID=' +
      employeeID,
      fetchOptions('PUT', { comment: commentData }, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][putGoalComment()] We could not reach the server:', error);
    return 500;
  }
};

// Delete Goal Comment
export const deleteGoalComment = async (
  companyID,
  commentID,
  employeeID,
  token,
  correlationId = null
) => {
  try {
    const response = await fetch(
      Configs.platformDeleteGoalCommentAPI +
      '?companyID=' +
      companyID +
      '&commentID=' +
      commentID +
      '&employeeID=' +
      employeeID,
      fetchOptions('DELETE', null, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][deleteGoalComment()] We could not reach the server:', error);
    return 500;
  }
};

// Platform development plan HTTP lives in src/queries/useGetDevelopment*.ts and src/queryMutations/use*DevelopmentPlan*.ts

// --------------------------------------------
// Logging
// --------------------------------------------
export const logErrorToBackend = async (token, errorData, correlationId = null) => {
  try {
    const response = await fetch(
      Configs.platformLogErrorAPI,
      fetchOptions('POST', errorData, token, 'json', correlationId)
    );
    return await handleResponse(response);
  } catch (error) {
    console.error('[apiCalls.js][logErrorToBackend()] We could not reach the server:', error);
    return 500;
  }
};

// --------------------------------------------
// PLG
// --------------------------------------------
export const getAccessTokenFromSlack = async (code, clientId, clientSecret) => {
  try {
    const url = Configs.slackExchangeTokenForCodeAPI;

    const formData = new FormData();
    formData.append('code', code);
    formData.append('client_id', clientId);
    formData.append('client_secret', clientSecret);
    if (Configs.devEnvironment)
      console.log(
        '[apiCalls.js][getAccessTokenFromSlack()] data: ',
        formData,
        clientId,
        clientSecret
      );
    const response = await fetch(url, {
      method: 'POST',
      body: formData,
    });

    if (response.ok) {
      const data = await response.json();
      if (Configs.devEnvironment)
        console.log('[apiCalls.js][getAccessTokenFromSlack()] data: ', data);
      return data;
    } else {
      console.error(
        '[apiCalls.js][getAccessTokenFromSlack()] We could not reach the server:',
        response
      );
      return response.status;
    }
  } catch (error) {
    console.error('[apiCalls.js][getAccessTokenFromSlack()] We could not reach the server:', error);
    return 500;
  }
};

export const sendSlackAccessTokenToBackend = async (slackResponse) => {
  try {
    const response = await fetch(
      Configs.platformPostSlackAuthResponseAPI,
      fetchOptions(
        'POST',
        slackResponse,
        import.meta.env.VITE_UNAUTHENTICATED_REQUESTS_API_TOKEN,
        'json'
      )
    );
    return await handleResponse(response);
  } catch (error) {
    console.error(
      '[apiCalls.js][getCCOaaSCompanyDetailsAIAnalysis()] We could not reach the server:',
      error
    );
    return 500;
  }
};